import { supabase } from './client'
import { requestEmailVerificationOTP, verifyEmailVerificationOTP } from './emailVerification.service'

export interface ProviderSignupData {
  email: string
  password: string
  name: string
  phone: string
  specialty: string
  licenseNumber: string
  experience: string
  gender: string
  dateOfBirth?: string
}

export interface ProviderLoginData {
  email: string
  password: string
}

export type ProviderAccountStatus =
  | 'document_pending'
  | 'pending_approval'
  | 'approved'
  | 'rejected'

export interface Provider {
  id: string
  auth_id: string
  name: string
  phone_number: string
  specialty: string
  license_number: string
  experience: string
  gender: string
  date_of_birth?: string
  account_status: ProviderAccountStatus
  is_verified: boolean
  is_active: boolean
  is_deleted: boolean
  profile_image_url: string | null
  created_at: string
  last_login_at?: string
  email?: string
  avg_rating?: number
  address?: string
  qualification?: string
  languages?: string[]
  specializations?: string[]
  bio?: string
  about?: string
}

export interface ProviderInductionProgress {
  provider_id: string
  current_chapter: number
  current_step: number
  ndpr_acknowledged: boolean
  safety_security_acknowledged: boolean
  platform_rules_acknowledged: boolean
  code_conduct_acknowledged: boolean
  terms_service_acknowledged: boolean
  app_usage_acknowledged: boolean
  is_completed: boolean
  completion_version: number
  completed_at: string | null
}

export interface ProviderInductionStatus {
  needsInduction: boolean
  isCompleted: boolean
  progress: ProviderInductionProgress | null
  error?: string
}

export type ProviderInductionChapterKey =
  | 'ndpr'
  | 'safety_security'
  | 'platform_rules'
  | 'code_conduct'
  | 'terms_service'
  | 'app_usage'

const INDUCTION_CHAPTER_COLUMNS: Record<ProviderInductionChapterKey, keyof ProviderInductionProgress> = {
  ndpr: 'ndpr_acknowledged',
  safety_security: 'safety_security_acknowledged',
  platform_rules: 'platform_rules_acknowledged',
  code_conduct: 'code_conduct_acknowledged',
  terms_service: 'terms_service_acknowledged',
  app_usage: 'app_usage_acknowledged',
}

const createDefaultInductionProgress = (providerId: string) => ({
  provider_id: providerId,
  current_chapter: 1,
  current_step: 1,
  ndpr_acknowledged: false,
  safety_security_acknowledged: false,
  platform_rules_acknowledged: false,
  code_conduct_acknowledged: false,
  terms_service_acknowledged: false,
  app_usage_acknowledged: false,
  is_completed: false,
  completion_version: 1,
  completed_at: null,
})

export interface ProviderAuthResponse {
  success: boolean
  message?: string
  error?: string
  provider?: Provider
}

export const providerAuthService = {
  async signup(data: ProviderSignupData): Promise<ProviderAuthResponse> {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: { role: 'provider' },
      },
    })

    if (authError) {
      return { success: false, error: authError.message }
    }

    if (!authData.user) {
      return { success: false, error: 'Failed to create account' }
    }

    const { data: providerRecord, error: providerError } = await supabase.from('providers').insert({
      auth_id: authData.user.id,
      name: data.name,
      phone_number: data.phone,
      specialty: data.specialty,
      license_number: data.licenseNumber,
      experience: data.experience,
      gender: data.gender,
      date_of_birth: data.dateOfBirth || null,
      account_status: 'document_pending',
      is_verified: false,
    }).select('id').single()

    if (providerError) {
      return { success: false, error: providerError.message }
    }

    // Create related records (best-effort)
    if (providerRecord?.id) {
      try { await supabase.from('provider_settings').insert({ provider_id: providerRecord.id }) } catch { /* ignore */ }
      try { await supabase.from('provider_earnings').insert({ provider_id: providerRecord.id }) } catch { /* ignore */ }
      try { await supabase.from('provider_wallets').insert({ provider_id: providerRecord.id }) } catch { /* ignore */ }
    }

    if (authData.user.email) {
      const verificationResult = await requestEmailVerificationOTP(authData.user.email, 'provider')
      if (!verificationResult.success) {
        return { success: false, error: verificationResult.error }
      }
    }

    return { success: true, message: 'Account created. Please verify your email.' }
  },

  async login(data: ProviderLoginData): Promise<ProviderAuthResponse> {
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    })

    if (authError) {
      return { success: false, error: authError.message }
    }

    if (!authData.user) {
      return { success: false, error: 'Login failed' }
    }

    const { data: provider, error: providerError } = await supabase
      .from('providers')
      .select('*')
      .eq('auth_id', authData.user.id)
      .single()

    if (providerError || !provider) {
      await supabase.auth.signOut()
      return { success: false, error: 'Invalid login details' }
    }

    if (provider.is_deleted) {
      await supabase.auth.signOut()
      return { success: false, error: 'Invalid login details' }
    }

    if (!provider.is_active) {
      await supabase.auth.signOut()
      return { success: false, error: 'Invalid login details' }
    }

    // Update last login
    await supabase
      .from('providers')
      .update({ last_login_at: new Date().toISOString() })
      .eq('auth_id', authData.user.id)

    return {
      success: true,
      provider: { ...provider, email: authData.user.email },
    }
  },

  async logout(): Promise<void> {
    await supabase.auth.signOut()
  },

  async getProfile(): Promise<Provider | null> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data: provider } = await supabase
      .from('providers')
      .select('*')
      .eq('auth_id', user.id)
      .maybeSingle()

    if (!provider) return null
    return { ...provider, email: user.email }
  },

  async verifyEmail(email: string, token: string): Promise<ProviderAuthResponse> {
    const result = await verifyEmailVerificationOTP(email, token, 'provider')
    return result.success
      ? { success: true, message: 'Email verified successfully' }
      : { success: false, error: result.error }
  },

  async resendVerificationEmail(email: string): Promise<ProviderAuthResponse> {
    const result = await requestEmailVerificationOTP(email, 'provider')
    return result.success
      ? { success: true, message: 'Verification email resent' }
      : { success: false, error: result.error }
  },

  async submitDocuments(medicalCertificate: File, governmentId: File): Promise<ProviderAuthResponse> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, error: 'Not authenticated' }

    const uploadFile = async (file: File, label: string) => {
      const ext = file.name.split('.').pop()
      const path = `${user.id}/${label}-${Date.now()}.${ext}`
      const { error } = await supabase.storage
        .from('provider-documents')
        .upload(path, file, { contentType: file.type })
      if (error) throw new Error(error.message)
      return supabase.storage.from('provider-documents').getPublicUrl(path).data.publicUrl
    }

    try {
      const [certUrl, idUrl] = await Promise.all([
        uploadFile(medicalCertificate, 'medical-cert'),
        uploadFile(governmentId, 'govt-id'),
      ])

      // Insert document records
      await supabase.from('provider_documents').insert([
        { provider_auth_id: user.id, document_type: 'medical_certificate', document_url: certUrl },
        { provider_auth_id: user.id, document_type: 'government_id', document_url: idUrl },
      ])

      // Update account status to pending_approval
      await supabase
        .from('providers')
        .update({ account_status: 'pending_approval' })
        .eq('auth_id', user.id)

      return { success: true, message: 'Documents submitted for review' }
    } catch (err) {
      return { success: false, error: (err as Error).message }
    }
  },

  async getAccountStatus(): Promise<ProviderAccountStatus | null> {
    const profile = await this.getProfile()
    return profile?.account_status ?? null
  },

  async getOrCreateInductionProgress(providerId?: string): Promise<{
    success: boolean
    progress?: ProviderInductionProgress
    error?: string
  }> {
    try {
      const provider = providerId
        ? ({ id: providerId } as Provider)
        : await this.getProfile()

      if (!provider?.id) {
        return { success: false, error: 'Provider not found' }
      }

      const { data: existing, error: selectError } = await supabase
        .from('provider_induction_progress')
        .select('*')
        .eq('provider_id', provider.id)
        .maybeSingle()

      if (selectError) {
        throw selectError
      }

      if (existing) {
        return { success: true, progress: existing as ProviderInductionProgress }
      }

      const { data: created, error: insertError } = await supabase
        .from('provider_induction_progress')
        .insert(createDefaultInductionProgress(provider.id))
        .select('*')
        .single()

      if (insertError) {
        throw insertError
      }

      return { success: true, progress: created as ProviderInductionProgress }
    } catch (error) {
      return { success: false, error: (error as Error).message || 'Failed to get induction progress' }
    }
  },

  async saveInductionProgress(
    currentChapter: number,
    currentStep: number,
  ): Promise<{ success: boolean; progress?: ProviderInductionProgress; error?: string }> {
    try {
      const provider = await this.getProfile()
      if (!provider?.id) {
        return { success: false, error: 'Provider not found' }
      }

      const ensured = await this.getOrCreateInductionProgress(provider.id)
      if (!ensured.success) {
        return { success: false, error: ensured.error || 'Failed to load induction progress' }
      }

      const { data: updated, error } = await supabase
        .from('provider_induction_progress')
        .update({
          current_chapter: Math.max(1, Math.min(6, currentChapter)),
          current_step: Math.max(1, currentStep),
        })
        .eq('provider_id', provider.id)
        .select('*')
        .single()

      if (error) {
        throw error
      }

      return { success: true, progress: updated as ProviderInductionProgress }
    } catch (error) {
      return { success: false, error: (error as Error).message || 'Failed to save induction progress' }
    }
  },

  async acknowledgeInductionChapter(
    chapter: ProviderInductionChapterKey,
    currentChapter: number,
    currentStep: number,
  ): Promise<{ success: boolean; progress?: ProviderInductionProgress; error?: string }> {
    try {
      const provider = await this.getProfile()
      if (!provider?.id) {
        return { success: false, error: 'Provider not found' }
      }

      const ensured = await this.getOrCreateInductionProgress(provider.id)
      if (!ensured.success) {
        return { success: false, error: ensured.error || 'Failed to load induction progress' }
      }

      const chapterColumn = INDUCTION_CHAPTER_COLUMNS[chapter]
      const { data: updated, error } = await supabase
        .from('provider_induction_progress')
        .update({
          current_chapter: Math.max(1, Math.min(6, currentChapter)),
          current_step: Math.max(1, currentStep),
          [chapterColumn]: true,
        })
        .eq('provider_id', provider.id)
        .select('*')
        .single()

      if (error) {
        throw error
      }

      return { success: true, progress: updated as ProviderInductionProgress }
    } catch (error) {
      return { success: false, error: (error as Error).message || 'Failed to acknowledge chapter' }
    }
  },

  async completeInduction(): Promise<{ success: boolean; progress?: ProviderInductionProgress; error?: string }> {
    try {
      const provider = await this.getProfile()
      if (!provider?.id) {
        return { success: false, error: 'Provider not found' }
      }

      const existing = await this.getOrCreateInductionProgress(provider.id)
      if (!existing.success || !existing.progress) {
        return { success: false, error: existing.error || 'Failed to load induction progress' }
      }

      if (existing.progress.is_completed) {
        return { success: true, progress: existing.progress }
      }

      const { data: updated, error } = await supabase
        .from('provider_induction_progress')
        .update({
          current_chapter: 6,
          current_step: 1,
          ndpr_acknowledged: true,
          safety_security_acknowledged: true,
          platform_rules_acknowledged: true,
          code_conduct_acknowledged: true,
          terms_service_acknowledged: true,
          app_usage_acknowledged: true,
          is_completed: true,
          completion_version: 1,
          completed_at: new Date().toISOString(),
        })
        .eq('provider_id', provider.id)
        .select('*')
        .single()

      if (error) {
        throw error
      }

      return { success: true, progress: updated as ProviderInductionProgress }
    } catch (error) {
      return { success: false, error: (error as Error).message || 'Failed to complete induction' }
    }
  },

  async getInductionStatus(): Promise<ProviderInductionStatus> {
    try {
      const provider = await this.getProfile()
      if (!provider?.id) {
        return {
          needsInduction: false,
          isCompleted: false,
          progress: null,
          error: 'Provider not found',
        }
      }

      const canAccessApp = !!provider.is_verified && provider.account_status === 'approved'

      if (!canAccessApp) {
        return {
          needsInduction: false,
          isCompleted: false,
          progress: null,
        }
      }

      const progressResult = await this.getOrCreateInductionProgress(provider.id)
      if (!progressResult.success || !progressResult.progress) {
        return {
          needsInduction: true,
          isCompleted: false,
          progress: null,
          error: progressResult.error || 'Failed to load induction progress',
        }
      }

      const isCompleted = !!progressResult.progress.is_completed

      return {
        needsInduction: !isCompleted,
        isCompleted,
        progress: progressResult.progress,
      }
    } catch (error) {
      return {
        needsInduction: true,
        isCompleted: false,
        progress: null,
        error: (error as Error).message || 'Failed to load induction status',
      }
    }
  },

  async sendPasswordResetEmail(email: string): Promise<ProviderAuthResponse> {
    const { error } = await supabase.auth.resetPasswordForEmail(email)
    if (error) return { success: false, error: error.message }
    return { success: true, message: 'Password reset code sent' }
  },

  async verifyPasswordResetOtp(email: string, token: string): Promise<ProviderAuthResponse> {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'recovery',
    })
    if (error) return { success: false, error: error.message }
    return { success: true, message: 'Code verified successfully' }
  },

  async updatePassword(newPassword: string): Promise<ProviderAuthResponse> {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) return { success: false, error: error.message }
    return { success: true, message: 'Password updated successfully' }
  },
}
